/**
 * Bundle 138 P0-4 - Age gate grandfathering modal.
 * Bundle 149b - extended to require first_name + last_name + DOB as
 * an atomic 3-field completion. Same non-dismissible pattern; same
 * pending_review branch on minor DOB submission.
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image in alt.
 */
import React, { useState } from 'react';
import { Loader2, ShieldAlert, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/utils/api';

// Bundle 149b - mirror the backend `require_full_name_or_reject`
// predicate. Rejects empty / whitespace / <2 char / non-letter start.
const NAME_RE = /^[^\W\d_][\w'\-\s]{1,}$/u;

const AgeGateModal = ({ open, onConfirmed, user }) => {
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [dob, setDob] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [pending, setPending] = useState(false);

  const validate = () => {
    const next = {};
    const fs = (firstName || '').trim();
    const ls = (lastName || '').trim();
    if (!fs) next.first_name = 'Please enter your first name.';
    else if (!NAME_RE.test(fs) || fs.length < 2)
      next.first_name = 'Please enter a valid first name (letters, hyphens, apostrophes; at least two characters).';
    if (!ls) next.last_name = 'Please enter your last name.';
    else if (!NAME_RE.test(ls) || ls.length < 2)
      next.last_name = 'Please enter a valid last name (letters, hyphens, apostrophes; at least two characters).';
    if (!dob || dob.length < 10) next.dob = 'Enter your date of birth as YYYY-MM-DD.';
    if (!confirmed) next._confirm = 'Please confirm the information is accurate.';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError('');
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setLoading(true);
    try {
      const { data } = await api.post('/profile/complete-basic-info', {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        dob,
      });
      if (data?.ok) {
        onConfirmed && onConfirmed(data);
      } else {
        setGlobalError('Unexpected response. Please try again.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.code;
      const field = detail?.field;
      const msg = typeof detail === 'string' ? detail : detail?.message;
      if (code === 'MINOR_NOT_ALLOWED') {
        setPending(true);
      } else if (code === 'FIRST_NAME_REQUIRED' || (code === 'NAME_INVALID' && field === 'first_name')) {
        setErrors((e0) => ({ ...e0, first_name: msg || 'Please enter a valid first name.' }));
      } else if (code === 'LAST_NAME_REQUIRED' || (code === 'NAME_INVALID' && field === 'last_name')) {
        setErrors((e0) => ({ ...e0, last_name: msg || 'Please enter a valid last name.' }));
      } else if (code === 'INCOMPLETE_PROFILE' && field === 'dob') {
        setErrors((e0) => ({ ...e0, dob: msg || 'Please enter a valid date of birth.' }));
      } else if (code === 'DOB_LOCKED') {
        setGlobalError('Date of birth is already on file. Contact support if incorrect.');
      } else {
        setGlobalError(msg || 'Could not save. Please try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={() => { /* not dismissible */ }}>
      <DialogContent
        className="max-w-md"
        data-testid="age-gate-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest font-medium mb-2">
            <ShieldAlert className="h-4 w-4" /> Complete your profile
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-primary-900">
            Please confirm your details
          </DialogTitle>
        </DialogHeader>

        {pending ? (
          <div data-testid="age-gate-pending" className="space-y-3">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Dostination is only available to guests aged 18 and over. If you believe this is
                an error, contact <a className="underline text-secondary-700" href="mailto:support@dostination.com">support@dostination.com</a>.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-primary-700">
              We need your full legal first name, last name, and date of birth to keep your account active.
            </p>
            <div className="space-y-2">
              <Label htmlFor="age-gate-first">First name</Label>
              <Input
                id="age-gate-first" value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="age-gate-first-name-input"
                required
              />
              {errors.first_name && (
                <div className="text-xs text-red-700" data-testid="age-gate-first-name-error">{errors.first_name}</div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="age-gate-last">Last name</Label>
              <Input
                id="age-gate-last" value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                data-testid="age-gate-last-name-input"
                required
              />
              {errors.last_name && (
                <div className="text-xs text-red-700" data-testid="age-gate-last-name-error">{errors.last_name}</div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="age-gate-dob">Date of birth</Label>
              <Input
                id="age-gate-dob" type="date" value={dob}
                onChange={(e) => setDob(e.target.value)}
                data-testid="age-gate-dob-input"
                required
              />
              {errors.dob && (
                <div className="text-xs text-red-700" data-testid="age-gate-dob-error">{errors.dob}</div>
              )}
            </div>
            <label className="flex items-start gap-2 text-sm text-primary-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="age-gate-confirm-checkbox"
              />
              <span>I confirm this information is accurate.</span>
            </label>
            {errors._confirm && (
              <div className="text-xs text-red-700">{errors._confirm}</div>
            )}
            {globalError ? (
              <Alert variant="destructive" data-testid="age-gate-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{globalError}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button
                type="submit"
                disabled={loading}
                data-testid="age-gate-submit-btn"
                className="w-full"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                ) : 'Continue'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgeGateModal;
